/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Android-style static console logger with leveled, gated output.
 *
 * Levels are ordered ERROR < WARN < DEBUG < INFO. A message is emitted only
 * when its level is at or below the configured {@link Log.defaultLogLevel},
 * which production builds may lower to ERROR. The console output format is
 * `[LEVEL] [tag] message` followed by any additional parameters.
 */
export class Log {
  public static readonly LogLevel: {
    ERROR: number;
    WARN: number;
    DEBUG: number;
    INFO: number;
  } = {
    ERROR: 0, // ERROR
    WARN: 1, // ERROR, WARN
    DEBUG: 2, // ERROR, WARN, DEBUG
    INFO: 3 // ERROR, WARN, DEBUG, INFO
  };

  private static defaultLogLevel: number = Log.LogLevel.INFO;

  /**
   * Set the active log level. Messages above this level are suppressed.
   * @param level - One of {@link Log.LogLevel}.
   */
  public static setLogLevel(level: number): void {
    Log.defaultLogLevel = level;
  }

  private static shouldLog(level: number): boolean {
    return level <= Log.defaultLogLevel;
  }

  /**
   * Debug level log.
   * @param tag - Class or component identifier.
   * @param message - Log message.
   * @param optionalParams - Additional values to log.
   */
  public static d(
    tag: string,
    message: string,
    ...optionalParams: unknown[]
  ): void {
    if (!Log.shouldLog(Log.LogLevel.DEBUG)) {
      return;
    }
    if (optionalParams.length === 0) {
      console.debug(`[DEBUG] [${tag}] ${message}`);
    } else {
      console.debug(`[DEBUG] [${tag}] ${message}`, ...optionalParams);
    }
  }

  /**
   * Info level log.
   * @param tag - Class or component identifier.
   * @param message - Log message.
   * @param optionalParams - Additional values to log.
   */
  public static i(
    tag: string,
    message: string,
    ...optionalParams: unknown[]
  ): void {
    if (!Log.shouldLog(Log.LogLevel.INFO)) {
      return;
    }
    if (optionalParams.length === 0) {
      console.info(`[INFO] [${tag}] ${message}`);
    } else {
      console.info(`[INFO] [${tag}] ${message}`, ...optionalParams);
    }
  }

  /**
   * Warning level log.
   * @param tag - Class or component identifier.
   * @param message - Log message.
   * @param optionalParams - Additional values to log.
   */
  public static w(
    tag: string,
    message: string,
    ...optionalParams: unknown[]
  ): void {
    if (!Log.shouldLog(Log.LogLevel.WARN)) {
      return;
    }
    if (optionalParams.length > 1) {
      console.warn(`[WARN] [${tag}] ${message}`, ...optionalParams);
    } else {
      console.warn(
        `[WARN] [${tag}] ${message}`,
        Log.firstDetail(optionalParams)
      );
    }
  }

  /**
   * Error level log.
   * @param tag - Class or component identifier.
   * @param message - Log message.
   * @param optionalParams - Additional values to log.
   */
  public static e(
    tag: string,
    message: string,
    ...optionalParams: unknown[]
  ): void {
    if (!Log.shouldLog(Log.LogLevel.ERROR)) {
      return;
    }
    if (optionalParams.length > 1) {
      console.error(`[ERROR] [${tag}] ${message}`, ...optionalParams);
    } else {
      console.error(
        `[ERROR] [${tag}] ${message}`,
        Log.firstDetail(optionalParams)
      );
    }
  }

  private static firstDetail(optionalParams: unknown[]): unknown {
    const detail: unknown =
      optionalParams.length === 0 ? undefined : optionalParams[0];
    return detail === undefined || detail === null ? "" : detail;
  }
}
